@if (initialized && customerBillingAccountDto) {
    <!-- Compact Header -->
    <div class="flex align-items-center justify-content-between mb-3">
        <div class="flex align-items-center gap-2">
            <span class="font-semibold">{{ customerBillingAccountDto.name || 'Account #' + customerBillingAccountDto.id }}</span>
            <p-tag [value]="getAccountStatusLabel()" [severity]="getAccountStatusSeverity()" styleClass="text-xs"></p-tag>
        </div>
        <p-button icon="pi pi-refresh" [loading]="loading" (onClick)="onRefreshBalance()" [rounded]="true" [text]="true" severity="secondary"></p-button>
    </div>

    <!-- Balance Summary -->
    <div class="surface-50 border-round p-3 mb-3">
        <div class="flex justify-content-between align-items-center mb-2">
            <span class="text-500">Balance</span>
            <span class="font-bold" [ngClass]="{'text-green-500': (customerBillingAccountDto.customerBillingAccountBalanceDto?.balance || 0) >= 0, 'text-red-500': (customerBillingAccountDto.customerBillingAccountBalanceDto?.balance || 0) < 0}">
                {{ customerBillingAccountDto.customerBillingAccountBalanceDto?.balance || 0 | currency: customerBillingAccountDto.currency }}
            </span>
        </div>
        <div class="flex justify-content-between align-items-center text-sm">
            <span class="text-500">Active</span>
            <span>{{ customerBillingAccountDto.customerBillingAccountBalanceDto?.activeBalance || 0 | currency: customerBillingAccountDto.currency }}</span>
        </div>
    </div>

    <!-- Status Indicators -->
    <div class="flex gap-2 mb-3">
        <!-- SEPA tag from sepaFailureState (base component). With no state for this account yet, a neutral
             placeholder says that the state is being loaded or could not be loaded: an unknown state
             must not look like an account without failures. getSepaSeverity() answers 'secondary' there.
             A failed read always leaves the state null, so the error branch needs no state check; the
             loading branch does, because a refresh of the same account keeps its state standing. -->
        @if (sepaFailureState?.activeFailure) {
            <p-tag [value]="'SEPA: ' + sepaFailureState.failCount" [severity]="getSepaSeverity()"
                   class="text-xs"></p-tag>
        } @else if (sepaFailureStateError) {
            <p-tag value="SEPA: unbekannt" icon="fa-regular fa-triangle-exclamation" [severity]="getSepaSeverity()"
                   pTooltip="SEPA-Status konnte nicht geladen werden." class="text-xs"></p-tag>
        } @else if (!sepaFailureState && sepaFailureStateLoading) {
            <p-tag value="SEPA: wird geladen" [severity]="getSepaSeverity()" class="text-xs"></p-tag>
        }
        @if (runningDunning) {
            <p-tag value="Mahnung" severity="danger" styleClass="text-xs"></p-tag>
        }
    </div>

    <!-- Bank Account (bm.CustomerBillingAccountBank time slice valid today) -->
    @if (customerBillingAccountDto.currentBankAccountDto?.customerBankAccountDto) {
        <div class="mb-3">
            <span class="text-500 text-sm block mb-1">Bankverbindung</span>
            <div class="font-mono text-sm">{{ customerBillingAccountDto.currentBankAccountDto.customerBankAccountDto.iban }}</div>
            <div class="text-500 text-xs">{{ customerBillingAccountDto.currentBankAccountDto.customerBankAccountDto.accountHolderName }}</div>
        </div>
    }

    <!-- Customer -->
    <div class="mb-3">
        <span class="text-500 text-sm block mb-1">Customer</span>
        <a class="text-primary cursor-pointer text-sm">{{ customerBillingAccountDto.customerDtoName }}</a>
    </div>

    <!-- Contracts Count -->
    <!--
      Counts visibleContractPaymentAccounts, NOT the raw contractPaymentAccounts list: the raw
      list also holds SUPERSEDED link rows (business key = contract alone, predecessors keep a
      real past endDate — see createBillingAccountObjectRequest in the base component), including
      contracts that moved to another billing account. This view has no toggle, so
      includeOldContracts stays at its base default (false) and the count reads "contracts
      running or future on this account" (a slice that has not started yet counts, an ended one
      does not) — consistent with the full view's Verträge tab.
    -->
    @if (visibleContractPaymentAccounts.length) {
        <div class="mb-3">
            <span class="text-500 text-sm block mb-1">Contracts</span>
            <span class="text-sm">{{ visibleContractPaymentAccounts.length }} associated</span>
        </div>
    }

    <!-- Recent Transactions Preview -->
    @if (customerBillingAccountDto.transactions?.length) {
        <div class="mb-3">
            <span class="text-500 text-sm block mb-2">Recent Transactions</span>
            @for (tx of customerBillingAccountDto.transactions.slice(0, 3); track tx.id) {
                <div class="flex justify-content-between align-items-center py-1 border-bottom-1 surface-border">
                    <div class="text-sm">
                        <span>{{ tx.valutaDate | date:'dd.MM' }}</span>
                        <span class="text-500 ml-2">{{ tx.transactionTypeDtoName }}</span>
                    </div>
                    <span class="text-sm font-semibold" [ngClass]="{'text-green-500': tx.transactionDirection === 0, 'text-red-500': tx.transactionDirection === 1}">
                        {{ tx.transactionDirection === 0 ? '+' : '-' }}{{ tx.amount | currency: tx.currency }}
                    </span>
                </div>
            }
        </div>
    }
} @else if (!initialized) {
    <div class="flex justify-content-center p-4">
        <p-progressSpinner [style]="{width: '30px', height: '30px'}"></p-progressSpinner>
    </div>
} @else {
    <!-- Nicht-gefunden-Zustand: Laden abgeschlossen, aber kein Datensatz -->
    <div class="flex flex-column align-items-center gap-2 p-4 text-500">
        <i class="fa-regular fa-circle-exclamation text-400 text-xl"></i>
        <span class="text-sm">Abrechnungskonto nicht gefunden.</span>
    </div>
}

